import { useCallback, useState, useEffect, useRef } from 'react';
import { downloadAuthenticatedFile, fetchWithAuth } from '../config/api';
import '../css/Phase3Results.css';
import { MOCK_MODE } from '../config/mock';
import EditableLabel from './EditableLabel';
import { useLabels } from '../config/labelsContext';
import { normalizeProgressMessage } from '../utils/progressMessage';
import {
  emptyQualityDistribution,
  getPatentDisplayCountInfo,
  parseQualityDistribution,
} from '../utils/patentDisplayCount';

interface Phase3ResultsProps {
  sessionId: string;
  workflowData: Record<string, unknown>;
  onRestart: () => void;
  onStatusChange?: (status: 'download' | 'completed') => void;
  setIsLoading?: (loading: boolean) => void;
  setLoadingMessage?: (message: string) => void;
  showAlert?: (message: string, title?: string) => void;
  initialSessionData?: Record<string, unknown> | null;
  readOnlyPreview?: boolean;
}

const Phase3Results = ({
  sessionId,
  workflowData,
  onRestart,
  onStatusChange,
  showAlert,
  initialSessionData,
  readOnlyPreview = false,
}: Phase3ResultsProps) => {
  const { labels } = useLabels();
  const downloadStartedMessage = 'ダウンロードを開始しました';
  const downloadReadyMessage = 'Excelファイルの準備が完了しました';
  const downloadFailedMessage = 'ダウンロードに失敗しました。再度お試しください';
  const parseInputInfo = (raw: unknown) => {
    const text = (raw || '').toString();
    const lines = text
      .split(/\r?\n/g)
      .map((l) => l.trim())
      .filter(Boolean);

    const out: Record<string, string> = {};
    for (const line of lines) {
      const idx = line.indexOf(':');
      if (idx < 0) continue;
      const k = line.slice(0, idx).trim();
      const v = line.slice(idx + 1).trim();
      if (!k || !v) continue;
      out[k] = v;
    }

    const usage = out[labels.phase3.inputUsageField] || '';
    const tech = out[labels.phase3.inputTechFeature] || '';
    const problem = out[labels.phase3.inputProblem] || '';
    const effect = out[labels.phase3.inputEffect] || '';

    return {
      usage,
      tech,
      problem,
      effect,
      hasStructured: Boolean(usage || tech || problem || effect),
      rawText: text,
    };
  };

  const alertUI = useCallback(
    (message: string) => {
      if (showAlert) return showAlert(message, labels.modals.noticeTitle);
      window.alert(message);
    },
    [showAlert, labels.modals.noticeTitle]
  );
  const [downloadUrl, setDownloadUrl] = useState('');
  const [sessionData, setSessionData] = useState<Record<string, unknown> | null>(null);
  const [inlineStatusMessage, setInlineStatusMessage] = useState('');
  const [inlineError, setInlineError] = useState('');
  const generatedForSessionRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const isDownloadCompleted = (data: Record<string, unknown> | null) =>
    Boolean((data?.download_completed_at as string | null | undefined) || '');

  useEffect(() => {
    if (!sessionData) return;
    onStatusChange?.(isDownloadCompleted(sessionData) ? 'completed' : 'download');
  }, [sessionData, onStatusChange]);

  useEffect(() => {
    if (!sessionId) {
      setSessionData(null);
      setDownloadUrl('');
      setInlineStatusMessage('');
      // Only reset to 'download' when there is no session (unmount case).
      onStatusChange?.('download');
      return;
    }

    if (readOnlyPreview) {
      const data = initialSessionData || null;
      setSessionData(data);
      setDownloadUrl('');
      setInlineError('');
      setInlineStatusMessage('');
      onStatusChange?.(isDownloadCompleted(data) ? 'completed' : 'download');
      return;
    }

    // Don't reset to 'download' here — if the caller already set 'completed'
    // (e.g. for finished sessions), we should preserve that until sessionData loads.

    // 同一sessionで二重に走らせない
    if (generatedForSessionRef.current === sessionId) return;
    generatedForSessionRef.current = sessionId;

    // 多重同時実行ガード（保険）
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    if (MOCK_MODE) {
      // Mock data based on prod session SESSION-20260407-e4d4a4451034 (EV、充電式移動体)
      // Varied assignees/scores here exist solely to exercise the 5-category pie chart.
      const patents: Array<{ id: string; title: string; assignees: string; score: string }> = [
        {
          id: 'P-1',
          title: 'Parking assistance device and parking assistance method',
          assignees: '株式会社東海理化電機製作所',
          score: '◎',
        },
        {
          id: 'P-2',
          title: 'マルチラインレーダー',
          assignees: 'Tokai Rika Co., Ltd.',
          score: '〇',
        },
        { id: 'P-3', title: '回転コンパクト光測距システム', assignees: 'Sony Corp.', score: '◎' },
        {
          id: 'P-4',
          title: 'Digital twin-based charging station control system',
          assignees: 'Toyota Motor Corp.',
          score: '◎',
        },
        { id: 'P-5', title: '光通信に基づくレーザレーダ', assignees: 'Denso Corp.', score: '〇' },
        {
          id: 'P-6',
          title: '距離推定装置、アンテナ装置、給電システム、給電装置、及び給電方法',
          assignees: 'Panasonic Corp.',
          score: '〇',
        },
        {
          id: 'P-7',
          title:
            'Versatile configurable robot/vehicle carrier for robot/vehicle docking, charging, transporting, and control',
          assignees: 'Honda Motor Co., Ltd.',
          score: '△',
        },
        {
          id: 'P-8',
          title:
            'Intelligent power wireless charging system for electric wheelchairs, robotic arm and ranging sensor thereof',
          assignees: 'Nissan Motor Co., Ltd.',
          score: '△',
        },
      ];
      const qualities = patents.map((p) => ({
        patent: { id: p.id, title: p.title, assignees: p.assignees },
        judge_result: {
          three_level_rating1: {
            result: { score: p.score },
          },
        },
      }));
      setSessionData({
        session_id: sessionId,
        status: 'completed',
        current_step: 23,
        title: 'EV、充電式移動体',
        final_patents: patents,
        selected_realization_ids: [
          '自律走行および自動ドッキングによる車両誘導',
          '光学センシングおよび画像解析による相対位置計測',
          '物理的な位置調整機構および回路構造の最適化',
        ],
        selected_formula_ids: ['FORMULA-3', 'FORMULA-5', 'FORMULA-10', 'FORMULA-11'],
        patent_qualities: qualities,
        user_input: `${labels.phase1.inputFieldUsage}: EV、充電式移動体\n${labels.phase1.inputFieldTech}: ワイヤレス充電を行う充電設備と車両に搭載した電池の位置合わせを行う\n${labels.phase1.inputFieldProblem}: ワイヤレス充電において充電電池が決められた正しい位置に来ないと充電の効率が低下する`,
        search_formulas: Array.from({ length: 4 }, (_, i) => ({ id: `FORMULA-${i + 1}` })),
        realization_methods: [
          {
            id: '自律走行および自動ドッキングによる車両誘導',
            name: '自律走行および自動ドッキングによる車両誘導',
          },
          {
            id: '光学センシングおよび画像解析による相対位置計測',
            name: '光学センシングおよび画像解析による相対位置計測',
          },
          {
            id: '物理的な位置調整機構および回路構造の最適化',
            name: '物理的な位置調整機構および回路構造の最適化',
          },
        ],
        pdf_files: [
          { file_name: 'AA17147823.pdf', title: 'JP 2017-147823 A' },
          { file_name: 'AA15231328.pdf', title: 'JP 2015-231328 A' },
          { file_name: 'AA14093824.pdf', title: 'JP 2014-093824 A' },
        ],
      });
      setDownloadUrl('mock://test-download');
      setInlineStatusMessage('');
      inFlightRef.current = false;
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      try {
        setSessionData(null);
        setDownloadUrl('');
        setInlineError('');
        setInlineStatusMessage(labels.phase3.loadingResultData);
        let readyUrl = '';
        const fallbackDownloadUrl = (filename = `patent_analysis_${sessionId}.xlsx`) =>
          `/api/phase3/download/${filename}?session_id=${encodeURIComponent(sessionId)}`;
        const resolveExportDownloadUrl = async (filename?: string) => {
          const fallbackUrl = fallbackDownloadUrl(filename);
          const exportRes = await fetchWithAuth(
            `/api/phase3/export?session_id=${encodeURIComponent(sessionId)}&format=excel`,
            { signal: controller.signal }
          );
          if (!exportRes.ok) return fallbackUrl;

          const exportData = (await exportRes.json()) as Record<string, unknown>;
          const fileUrl = (exportData.file_url as string | undefined) || '';
          return fileUrl || fallbackUrl;
        };

        // Poll until the session reaches a ready state (step>=19 or status=completed).
        let ss: Record<string, unknown> | null = null;
        let sessionRetries = 0;
        for (;;) {
          const ssRes = await fetchWithAuth(
            `/api/phase3/session/${encodeURIComponent(sessionId)}`,
            { signal: controller.signal }
          );
          if (!ssRes.ok) {
            sessionRetries += 1;
            if (sessionRetries >= 5) throw new Error(labels.phase3.sessionFetchFailed);
            await new Promise((r) => setTimeout(r, 5000));
            if (cancelled) return;
            continue;
          }
          sessionRetries = 0;
          ss = (await ssRes.json()) as Record<string, unknown>;
          if (cancelled) return;

          const st = (ss.status as string | undefined) || '';
          const step = Number(ss.current_step ?? 0);
          if (step >= 19 || st === 'completed') {
            setSessionData(ss);
            break;
          }
          setInlineStatusMessage(labels.phase3.loadingQualityWaiting);
          await new Promise((r) => setTimeout(r, 5000));
          if (cancelled) return;
        }
        if (!ss || cancelled) return;

        const st = (ss.status as string | undefined) || '';

        if (st === 'completed') {
          readyUrl = await resolveExportDownloadUrl();
          setDownloadUrl(readyUrl);
          setInlineStatusMessage(downloadReadyMessage);
          return;
        }

        // Avoid using the global loading modal here.
        // The Excel generation can take long and updates the message frequently,
        // which can feel like "flicker" if the overlay toggles.
        setInlineStatusMessage(labels.phase3.loadingExcelUrl);

        // 1) Try fast path: if the Excel already exists on S3, just get a presigned URL.
        try {
          for (;;) {
            const exportRes = await fetchWithAuth(
              `/api/phase3/export?session_id=${encodeURIComponent(sessionId)}&format=excel`,
              { signal: controller.signal }
            );

            if (exportRes.status === 400) {
              const errorData = await exportRes.json().catch(() => ({ detail: '' }));
              setInlineStatusMessage(errorData.detail || labels.phase3.loadingQualityWaiting);
              await new Promise((r) => setTimeout(r, 5000));
              if (cancelled) return;
              continue;
            }

            if (exportRes.ok) {
              const exportData = (await exportRes.json()) as Record<string, unknown>;
              const url = (exportData.file_url as string | undefined) || '';
              if (url) {
                readyUrl = url;
                setDownloadUrl(readyUrl);
                setInlineStatusMessage(downloadReadyMessage);
              }
            }
            break;
          }
        } catch {
          // ignore and fall back to job-based generation
        }

        // 2) If we still don't have a download URL, start (or reuse) Excel generation job.
        let jobId = '';
        if (!readyUrl) {
          const startRes = await fetchWithAuth(
            `/api/phase3/generate-excel?session_id=${encodeURIComponent(sessionId)}`,
            { method: 'POST', signal: controller.signal }
          );

          if (startRes.status === 400) {
            const errorData = await startRes.json().catch(() => ({ detail: '' }));
            setInlineStatusMessage(errorData.detail || labels.phase3.excelGenNotReady);
            await new Promise((r) => setTimeout(r, 5000));
            if (cancelled) return;
            // Retry the whole run() from the top so session state is re-fetched.
            inFlightRef.current = false;
            generatedForSessionRef.current = null;
            run();
            return;
          }

          if (!startRes.ok) throw new Error(labels.phase3.excelGenFailed);
          const startData = (await startRes.json()) as Record<string, unknown>;
          jobId = (startData.job_id as string | undefined) || '';

          // 3) Poll job status until succeeded/failed
          if (!jobId) throw new Error(labels.phase3.excelJobStartFailed);
          // Do not block the whole screen while waiting for Excel generation.
          // Users should be able to navigate away (restart) even if this takes long.
          let delayMs = 3000;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const jobRes = await fetchWithAuth(`/api/jobs/${encodeURIComponent(jobId)}`, {
              signal: controller.signal,
            });
            if (!jobRes.ok) throw new Error(labels.phase3.excelJobStatusFailed);
            const job = (await jobRes.json()) as Record<string, unknown>;
            if (cancelled) return;

            const cur = Number(job.progress_current ?? 0);
            const total = job.progress_total != null ? Number(job.progress_total) : null;
            const msg = normalizeProgressMessage(
              (job.progress_message as string | undefined) || labels.common.processing
            );
            setInlineStatusMessage(total ? `${msg} (${cur}/${total})` : msg);

            const jobStatus = (job.status as string | undefined) || '';
            if (jobStatus === 'succeeded') {
              const result = (job.result as Record<string, unknown> | undefined) || {};
              const excelFile = (result.excel_file as string | undefined) || '';
              if (excelFile) {
                readyUrl = await resolveExportDownloadUrl(excelFile);
                setDownloadUrl(readyUrl);
                setInlineStatusMessage(downloadReadyMessage);
              }
              break;
            }
            if (jobStatus === 'failed') {
              throw new Error(
                (job.error_message as string | undefined) || labels.phase3.excelGenFailed
              );
            }

            await new Promise((r) => setTimeout(r, delayMs));
            delayMs = Math.min(Math.floor(delayMs * 1.2), 15000);
          }
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'AbortError') return;

        // eslint-disable-next-line no-console
        console.error('結果生成に失敗しました:', error);
        const msg = error instanceof Error ? error.message : labels.phase3.resultGenFailed;
        alertUI(msg);
        setInlineError(msg);
        setInlineStatusMessage('');
        setSessionData(null);
      } finally {
        inFlightRef.current = false;
        // Keep inline status message for the screen; do not toggle the global overlay.
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
      inFlightRef.current = false;
      // Do NOT reset generatedForSessionRef here — it prevents duplicate runs
      // when React StrictMode or parent re-renders cause unmount/remount.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertUI, initialSessionData, onStatusChange, readOnlyPreview, sessionId]);

  const handleDownload = async () => {
    if (!downloadUrl) return;
    const completedAt = new Date().toISOString();
    setInlineError('');
    setInlineStatusMessage(downloadStartedMessage);
    setSessionData((prev) => (prev ? { ...prev, download_completed_at: completedAt } : prev));
    onStatusChange?.('completed');
    if (MOCK_MODE && downloadUrl.startsWith('mock://')) {
      const mockLines = [
        'Patent map mock download',
        `session_id: ${sessionId}`,
        `downloaded_at: ${new Date().toISOString()}`,
        '',
        'This is a mock file generated for UI testing.',
      ];
      const blob = new Blob([mockLines.join('\n')], { type: 'text/plain;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `patent_map_mock_${sessionId}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      return;
    }
    // Relative `/api/phase3/download/...` URLs require the Cognito Authorization
    // header, which window.open cannot attach, so fetch the body via fetchWithAuth
    // and trigger a blob download instead.
    try {
      await downloadAuthenticatedFile(downloadUrl, `patent_analysis_${sessionId}.xlsx`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Excel download failed:', e);
      setInlineStatusMessage('');
      setInlineError(downloadFailedMessage);
      alertUI(labels.dashboard.downloadError);
      return;
    }
    // Mark session as completed after user initiates download.
    fetchWithAuth(`/api/phase3/mark-downloaded?session_id=${encodeURIComponent(sessionId)}`, {
      method: 'POST',
    }).catch(() => {});
  };

  if (!sessionData) {
    const waitingExcel = !downloadUrl;

    const handleRetry = () => {
      setInlineError('');
      inFlightRef.current = false;
      generatedForSessionRef.current = null;
      setInlineStatusMessage(labels.phase3.loadingResultData);
      // Force re-mount by toggling a key isn't possible here,
      // but we can just re-run the effect by clearing the ref and updating state.
      setSessionData(null);
      // The useEffect won't re-trigger from sessionData alone,
      // so call the fetch logic directly via a minimal inline runner.
      const ctrl = new AbortController();
      (async () => {
        try {
          const ssRes = await fetchWithAuth(
            `/api/phase3/session/${encodeURIComponent(sessionId)}`,
            { signal: ctrl.signal }
          );
          if (!ssRes.ok) throw new Error(labels.phase3.sessionFetchFailed);
          const ss = (await ssRes.json()) as Record<string, unknown>;
          setSessionData(ss);
          // If session loaded, let the main effect handle the rest on next mount cycle.
          generatedForSessionRef.current = null;
          inFlightRef.current = false;
        } catch (e) {
          if (e instanceof DOMException && e.name === 'AbortError') return;
          const msg = e instanceof Error ? e.message : labels.phase3.resultGenFailed;
          setInlineError(msg);
          setInlineStatusMessage('');
        }
      })();
    };

    return (
      <div className="phase3-results">
        <div className="phase-card">
          <h2 className="phase-title phase4-section-title">
            <span className="phase-title-step">
              <EditableLabel labelKey="phase3.stepLabel" defaultValue="Step4:" />
            </span>
            <span className="phase-title-text">
              <EditableLabel labelKey="phase3.title" defaultValue="特許マップ ダウンロード" />
            </span>
          </h2>
          {inlineError ? (
            <div className="loading-state" style={{ padding: '24px 0' }}>
              <p style={{ color: '#c0392b', fontWeight: 600 }}>{inlineError}</p>
              <button
                type="button"
                onClick={handleRetry}
                style={{
                  marginTop: 12,
                  padding: '8px 20px',
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                <EditableLabel labelKey="phase3.retryButton" defaultValue="🔄 再試行" />
              </button>
            </div>
          ) : (
            <div className="loading-state" style={{ padding: '24px 0' }}>
              <span className="spinner-large"></span>
              <p>
                {waitingExcel ? (
                  inlineStatusMessage || (
                    <EditableLabel
                      labelKey="phase3.loadingExcelUrl"
                      defaultValue="Excelファイルを準備しています"
                    />
                  )
                ) : (
                  <EditableLabel
                    labelKey="phase3.loadingResultData"
                    defaultValue="特許マップの結果を読み込んでいます"
                  />
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const patentQualities = Array.isArray(sessionData.patent_qualities)
    ? (sessionData.patent_qualities as Array<{
        patent: { id: string; assignees?: string };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        judge_result: Record<string, { result: any }>;
      }>)
    : [];
  const selectedRealizationIds = Array.isArray(sessionData.selected_realization_ids)
    ? (sessionData.selected_realization_ids as unknown[])
    : [];
  const selectedFormulaIds = Array.isArray(sessionData.selected_formula_ids)
    ? (sessionData.selected_formula_ids as unknown[])
    : [];
  const generatedSearchFormulas = Array.isArray(sessionData.search_formulas)
    ? (sessionData.search_formulas as unknown[])
    : selectedFormulaIds;
  const realizationMethods = Array.isArray(sessionData.realization_methods)
    ? (sessionData.realization_methods as unknown[])
    : selectedRealizationIds;
  const summarizedQualityDistribution = parseQualityDistribution(sessionData.quality_distribution);

  // Company-aware classification: patterns must stay in sync with backend
  // `_is_our_company` in backend/utils/excel_generator.py so the pie chart
  // and the Excel legend categorize patents identically.
  const OUR_COMPANY_KEYWORDS = ['東海理化', 'tokai rika'];
  const isOurCompany = (assignees: string | undefined): boolean => {
    const s = (assignees || '').toLowerCase();
    if (!s) return false;
    return OUR_COMPANY_KEYWORDS.some((k) => s.includes(k));
  };

  const qualityDistribution = (() => {
    if (patentQualities.length === 0 && summarizedQualityDistribution) {
      return summarizedQualityDistribution;
    }

    const dist = emptyQualityDistribution();
    for (const q of patentQualities) {
      const score = q.judge_result?.['three_level_rating1']?.result?.score;
      const ours = isOurCompany(q.patent?.assignees);
      if (score === '◎') {
        if (ours) dist.oursStrong += 1;
        else dist.othersStrong += 1;
      } else if (score === '〇') {
        if (ours) dist.oursNormal += 1;
        else dist.othersNormal += 1;
      } else if (score === '△') {
        dist.poor += 1;
      }
    }
    dist.total =
      dist.oursStrong + dist.oursNormal + dist.othersStrong + dist.othersNormal + dist.poor;
    return dist;
  })();
  const displayedPatentCount = getPatentDisplayCountInfo(sessionData, qualityDistribution).count;
  const qualityTotal = Math.max(qualityDistribution.total, 1);

  const inputText = (workflowData.userInput as string) || (sessionData.user_input as string) || '';
  const inputInfo = parseInputInfo(inputText);

  return (
    <div className="phase3-results">
      <div className="phase-card">
        <h2 className="phase-title phase4-section-title">
          <span className="phase-title-step">
            <EditableLabel labelKey="phase3.stepLabel" defaultValue="Step4:" />
          </span>
          <span className="phase-title-text">
            <EditableLabel labelKey="phase3.title" defaultValue="特許マップ ダウンロード" />
          </span>
        </h2>
        <div className="results-header completion-header">
          <button
            onClick={handleDownload}
            className="completion-action-btn"
            disabled={!downloadUrl || readOnlyPreview}
          >
            <EditableLabel
              labelKey="phase3.downloadMapButton"
              defaultValue="作成した特許マップダウンロードする"
            />
          </button>
          <button onClick={onRestart} className="completion-action-btn completion-finish-btn">
            <EditableLabel
              labelKey="phase3.finishButton"
              defaultValue="特許マップの作成処理を終了する"
            />
          </button>
        </div>
        {readOnlyPreview && (
          <div className="download-inline-message" role="status">
            管理者プレビューのため、ダウンロードや状態更新は実行しません。
          </div>
        )}
        {(inlineStatusMessage || inlineError) && (
          <div className={`download-inline-message${inlineError ? ' error' : ''}`} role="status">
            {inlineError || inlineStatusMessage}
          </div>
        )}

        <div className="summary-section">
          <h3>
            <EditableLabel labelKey="phase3.summaryTitle" defaultValue="特許マップの概要" />
          </h3>
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-value">{selectedFormulaIds.length}</div>
              <div className="summary-label">
                <EditableLabel
                  labelKey="phase3.searchFormulas"
                  defaultValue="特許検索式（収集式）を選択した数"
                />
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-value">{selectedRealizationIds.length}</div>
              <div className="summary-label">
                <EditableLabel
                  labelKey="phase3.realizationMethods"
                  defaultValue="特許マップに掲載する対象技術を選択した数"
                />
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-value">{displayedPatentCount}</div>
              <div className="summary-label">
                <EditableLabel
                  labelKey="phase3.totalPatents"
                  defaultValue="特許マップに掲載した特許の総数"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="reference-section">
          <div className="reference-header">
            <EditableLabel labelKey="phase3.referenceTitle" defaultValue="参考情報" />
          </div>

          <div className="quality-distribution">
            <h3>
              <EditableLabel
                labelKey="phase3.qualityDistributionTitle"
                defaultValue="特許良否判定分布"
              />
            </h3>
            <div className="pie-chart-container">
              <svg viewBox="0 0 200 200" className="pie-chart">
                {(() => {
                  const total = qualityTotal || 1;
                  // Colors must stay in sync with the Excel legend fills defined
                  // in backend/utils/excel_generator.py (OUR_STRONG_FILL etc.).
                  const slices: Array<{ value: number; fill: string; stroke?: string }> = [
                    { value: qualityDistribution.oursStrong, fill: '#1D4ED8' },
                    { value: qualityDistribution.oursNormal, fill: '#93C5FD' },
                    { value: qualityDistribution.othersStrong, fill: '#F00000' },
                    { value: qualityDistribution.othersNormal, fill: '#FCA5A5' },
                    { value: qualityDistribution.poor, fill: '#FFFFFF', stroke: '#9CA3AF' },
                  ];

                  const toXY = (deg: number) => {
                    const rad = ((deg - 90) * Math.PI) / 180;
                    return [100 + 80 * Math.cos(rad), 100 + 80 * Math.sin(rad)];
                  };

                  const arc = (
                    start: number,
                    end: number,
                    fill: string,
                    stroke?: string,
                    key?: string
                  ) => {
                    if (end - start >= 360) {
                      return (
                        <circle
                          key={key}
                          cx={100}
                          cy={100}
                          r={80}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={stroke ? 1 : 0}
                        />
                      );
                    }
                    const [sx, sy] = toXY(start);
                    const [ex, ey] = toXY(end);
                    const large = end - start > 180 ? 1 : 0;
                    return (
                      <path
                        key={key}
                        d={`M 100 100 L ${sx} ${sy} A 80 80 0 ${large} 1 ${ex} ${ey} Z`}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={stroke ? 1 : 0}
                      />
                    );
                  };

                  let cursor = 0;
                  return (
                    <>
                      {slices.map((s, i) => {
                        if (s.value <= 0) return null;
                        const start = cursor;
                        const end = cursor + (s.value / total) * 360;
                        cursor = end;
                        return arc(start, end, s.fill, s.stroke, `slice-${i}`);
                      })}
                    </>
                  );
                })()}
              </svg>
              <div className="pie-legend">
                <div className="pie-legend-item">
                  <span className="pie-dot" style={{ background: '#1D4ED8' }}></span>
                  <EditableLabel labelKey="phase3.qualityOursStrong" defaultValue="◎ TR有力特許" />
                  <span className="pie-legend-value">
                    {qualityDistribution.oursStrong}
                    <EditableLabel labelKey="phase3.qualityUnit" defaultValue="件" />(
                    {qualityTotal
                      ? Math.round((qualityDistribution.oursStrong / qualityTotal) * 100)
                      : 0}
                    %)
                  </span>
                </div>
                <div className="pie-legend-item">
                  <span className="pie-dot" style={{ background: '#93C5FD' }}></span>
                  <EditableLabel
                    labelKey="phase3.qualityOursNormal"
                    defaultValue="〇 TR標準的特許"
                  />
                  <span className="pie-legend-value">
                    {qualityDistribution.oursNormal}
                    <EditableLabel labelKey="phase3.qualityUnit" defaultValue="件" />(
                    {qualityTotal
                      ? Math.round((qualityDistribution.oursNormal / qualityTotal) * 100)
                      : 0}
                    %)
                  </span>
                </div>
                <div className="pie-legend-item">
                  <span className="pie-dot" style={{ background: '#F00000' }}></span>
                  <EditableLabel
                    labelKey="phase3.qualityOthersStrong"
                    defaultValue="◎ 他社有力特許"
                  />
                  <span className="pie-legend-value">
                    {qualityDistribution.othersStrong}
                    <EditableLabel labelKey="phase3.qualityUnit" defaultValue="件" />(
                    {qualityTotal
                      ? Math.round((qualityDistribution.othersStrong / qualityTotal) * 100)
                      : 0}
                    %)
                  </span>
                </div>
                <div className="pie-legend-item">
                  <span className="pie-dot" style={{ background: '#FCA5A5' }}></span>
                  <EditableLabel
                    labelKey="phase3.qualityOthersNormal"
                    defaultValue="〇 他社標準的特許"
                  />
                  <span className="pie-legend-value">
                    {qualityDistribution.othersNormal}
                    <EditableLabel labelKey="phase3.qualityUnit" defaultValue="件" />(
                    {qualityTotal
                      ? Math.round((qualityDistribution.othersNormal / qualityTotal) * 100)
                      : 0}
                    %)
                  </span>
                </div>
                <div className="pie-legend-item">
                  <span
                    className="pie-dot"
                    style={{ background: '#FFFFFF', border: '1px solid #9CA3AF' }}
                  ></span>
                  <EditableLabel labelKey="phase3.qualityPoor" defaultValue="△ 低影響度 特許" />
                  <span className="pie-legend-value">
                    {qualityDistribution.poor}
                    <EditableLabel labelKey="phase3.qualityUnit" defaultValue="件" />(
                    {qualityTotal ? Math.round((qualityDistribution.poor / qualityTotal) * 100) : 0}
                    %)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="workflow-summary">
          <h3>
            <EditableLabel
              labelKey="phase3.workflowSummaryTitle"
              defaultValue="ワークフロー実行サマリー"
            />
          </h3>
          <div className="workflow-steps">
            <div className="workflow-step completed">
              <span className="step-icon">✓</span>
              <div>
                <strong>
                  <EditableLabel
                    labelKey="phase3.phase1InputInfo"
                    defaultValue="Step1: 分野の指定"
                  />
                </strong>
                <div className="input-info">
                  <div className="input-info-row">
                    <div className="input-info-label">
                      <EditableLabel labelKey="phase3.inputUsageField" defaultValue="利用分野" />
                    </div>
                    <div className="input-info-value">
                      {inputInfo.hasStructured ? inputInfo.usage : inputInfo.rawText}
                    </div>
                  </div>
                  <div className="input-info-row">
                    <div className="input-info-label">
                      <EditableLabel labelKey="phase3.inputTechFeature" defaultValue="技術的特徴" />
                    </div>
                    <div className="input-info-value">{inputInfo.tech}</div>
                  </div>
                  <div className="input-info-row">
                    <div className="input-info-label">
                      <EditableLabel labelKey="phase3.inputProblem" defaultValue="課題" />
                    </div>
                    <div className="input-info-value">{inputInfo.problem}</div>
                  </div>
                  <div className="input-info-row">
                    <div className="input-info-label">
                      <EditableLabel labelKey="phase3.inputEffect" defaultValue="効果作用" />
                    </div>
                    <div className="input-info-value">{inputInfo.effect}</div>
                  </div>
                  {Array.isArray(sessionData.pdf_files) &&
                    (sessionData.pdf_files as unknown[]).length > 0 && (
                      <div className="input-info-row">
                        <div className="input-info-label">
                          <EditableLabel
                            labelKey="phase3.inputPdfFiles"
                            defaultValue="特許例（PDF）"
                          />
                        </div>
                        <div className="input-info-value">
                          {(sessionData.pdf_files as unknown[]).length}件の特許例を入力
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
            <div className="workflow-step completed">
              <span className="step-icon">✓</span>
              <div>
                <strong>
                  <EditableLabel
                    labelKey="phase3.phase2bFormulaGeneration"
                    defaultValue="Step2: 特許情報の収集"
                  />
                </strong>
                <p>
                  ・{generatedSearchFormulas.length}件の検索式案から、{selectedFormulaIds.length}
                  件を選択し、{displayedPatentCount}
                  件の特許データを収集
                </p>
              </div>
            </div>
            <div className="workflow-step completed">
              <span className="step-icon">✓</span>
              <div>
                <strong>
                  <EditableLabel
                    labelKey="phase3.phase2cRealizationExtraction"
                    defaultValue="Step3: 特許マップ作成"
                  />
                </strong>
                <p>
                  ・{realizationMethods.length}件の技術項目（特許マップの技術の大分類案）から、
                  {selectedRealizationIds.length}件を特許マップに掲載する技術として選択
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Phase3Results;
